import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import Popout from 'components/popout';
import ProjectName from 'components/project-name';
import TaskGroupName from 'components/task-group-name';
import TaskGroupSelector from 'components/TaskGroupSelector';
import ProjectSelector from 'components/ProjectSelector';

import IconClose from 'icons/icon-close';
import IconArrowDown from 'icons/icon-arrow-down';

const ProjectNameWithWrapper = ({ id }) => (
    <span className="with-icon-after"><ProjectName id={id} /></span>
)

const SelectTaskGroupPopout = ({ selected, projectId, selectClassName = '', xAlign, defaultXPosition, inline, withHead = true, title, withProject = true, canSelectSame = true, itemComponent, onAdditionalAction, additionalText, withoutSelectTrigger, onConfirm, onComplete, onShow, onHide, onSelect, provideController, children }) => {
    const { t } = useTranslation(['task', 'common']);
    const [visible, setVisible] = useState(false);
    const [selectingProject, setSelectingProject] = useState(!projectId);
    const [projectIdState, setProjectIdState] = useState(projectId);
    const [selectedState, setSelectedState] = useState(selected);
    const popoutCtrl = useRef(null);

    const show = useCallback(() => {
        popoutCtrl.current?.show();
    }, [popoutCtrl]);

    const hide = useCallback(() => {
        popoutCtrl.current?.hide();
    }, [popoutCtrl]);

    const handlePopoutOpenClick = useCallback(() => {
        show();
    }, [show]);

    const handlePopoutCloseClick = useCallback(() => {
        hide();
    }, [hide]);

    const selectElement = useMemo(() => {
        if (withoutSelectTrigger) {
            return null;
        }

        if (children) {
            return React.Children.map(children, child => 
                React.cloneElement(child, {
                    className: (child.props.className || '') + (visible ? ' selected disable-tooltip' : ''),
                    onClick: handlePopoutOpenClick
                })
            );
        }

        return (
            <div onClick={handlePopoutOpenClick} className={'resource-selector-select ' + selectClassName + (visible ? ' selected disable-tooltip' : '')}>
                {
                    selectedState ? (
                        <TaskGroupName id={selectedState} />
                    ) : (
                        <span className="placeholder">{ t('Select a status') }...</span>
                    )
                }

                <IconArrowDown />
            </div>
        )
    }, [withoutSelectTrigger, children, selectClassName, selectedState, visible, handlePopoutOpenClick, t]);

    const handlePopoutShow = useCallback(() => {
        setVisible(true);

        onShow && onShow();
    }, [onShow]);

    const handlePopoutHide = useCallback(() => {
        setVisible(true);

        onHide && onHide()
    }, [onHide]);

    const handleTaskGroupSelect = useCallback(id => {
        setSelectedState(id);

        if (onSelect) {
            onSelect({
                id,
                projectId: projectIdState
            });

            hide();
        }
    }, [onSelect, projectIdState, hide]);

    const handleConfirmClick = useCallback(() => {
        onConfirm && onConfirm({
            id: selectedState,
            projectId: projectIdState
        });

        hide();
    }, [selectedState, projectIdState, onConfirm, hide]);

    const handleProjectChangeClick = useCallback(() => {
        setSelectingProject(true);
    }, []);

    const handleProjectSelect = useCallback(projectId => {
        setProjectIdState(projectId);
        setSelectingProject(false);
        setSelectedState(null);
    }, []);

    const handleAdditionalActionClick = useCallback(() => {
        onAdditionalAction();
        hide();
    }, [onAdditionalAction, hide]);

    useEffect(() => {
        const ctrl = {
            show: () => show(),
            hide: () => hide()
        };

        if (provideController) {
            if ('current' in provideController) {
                provideController.current = ctrl;
            } else {
                provideController && provideController(ctrl);
            }
        }

        return () => {
            if (provideController) {
                if ('current' in provideController) {
                    provideController.current = null;
                } else {
                    provideController && provideController(null);
                }
            }
        }
    }, [provideController]);

    return (
        <React.Fragment>
            { selectElement }

            <Popout
                className="no-arrow xxsmall popout-resource-selector"
                provideController={popoutCtrl}
                onShow={handlePopoutShow}
                onHide={handlePopoutHide}
                xAlign={xAlign}
                defaultXPosition={defaultXPosition}
                closeOnEveryClick={true}
                inline={inline}
            >
                {
                    withHead && title ? (
                        <div className="popout-head">
                            { title }

                            <button className="btn btn-icon btn-small popout-close" onClick={handlePopoutCloseClick}>
                                <IconClose />
                            </button>
                        </div>
                    ) : null
                }

                <div className="popout-main">
                    {
                        selectingProject ? (
                            <ProjectSelector selected={projectIdState} onSelect={handleProjectSelect} />
                        ) : (
                            <React.Fragment>
                                {
                                    withProject ? (
                                        <div className="popout-resource-selector-extra">
                                            <Trans
                                                i18nKey="project:In Project"
                                                components={{
                                                    strong: <strong className="clickable text-colored" onClick={handleProjectChangeClick} />,
                                                    ProjectName: <ProjectNameWithWrapper id={projectIdState} />,
                                                    IconArrowDown: <IconArrowDown size="14px" />
                                                }}
                                            />
                                        </div>
                                    ) : null
                                }

                                <TaskGroupSelector
                                    projectId={projectIdState}
                                    selected={selectedState}
                                    autoSelectFirst={Boolean(onConfirm)}
                                    onSelect={handleTaskGroupSelect}
                                    itemComponent={itemComponent}
                                />

                                {
                                    onComplete ? (
                                        <div className="popout-resource-selector-action" onClick={onComplete}>
                                            { t('actions.Mark task complete') }
                                        </div>
                                    ) : null
                                }
                            </React.Fragment>
                        )
                    }
                </div>

                {
                    onConfirm || onAdditionalAction ? (
                        <div className="popout-foot">
                            {
                                onAdditionalAction ? (
                                    <button className="btn btn-small btn-secondary btn-block" onClick={handleAdditionalActionClick}>{ additionalText }</button>
                                ) : null
                            }

                            {
                                onConfirm ? (
                                    <div className="popout-actions">
                                        <button className="btn btn-small btn-secondary" onClick={handlePopoutCloseClick}>{ t('common:Cancel') }</button>

                                        <button className="btn btn-small btn-success" disabled={(!canSelectSame && selectedState === selected) || !selectedState} onClick={handleConfirmClick}>{ t('common:Confirm') }</button>
                                    </div>
                                ) : null
                            }
                        </div>
                    ) : null
                }
            </Popout>
        </React.Fragment>
    );
}

export default SelectTaskGroupPopout;
